import { Info } from "@mui/icons-material";
import { Avatar, Box, Button, Card, CardContent, Divider, Typography } from "@mui/material";
import { BrandEnum } from "../../../_common/enum/brand.enum";
import { IMortgageQuote } from "../../../_common/interface/mortgageQuote/iMortgageQuote.interface";
import {
    getBrandCallbackLink,
    getBrandLogoSymbol,
    getBrandPrimaryColor,
    getBrandSecondaryColor,
    getBrandVibrantColor,
} from "../../../_helpers/brand.helper";
import { getMoneyFormat } from "../../../_helpers/format.helper";
import { isGreenMortgage } from "../../../_helpers/lowQuote.format.helper";
import { useBoxStyles } from "../../../_styles/box.styles";
import { QuoteProviderLogoView } from "../../quotes/quoteProviderLogo-view";


export function MortgagesQuoteCardDesktop(params: { item: IMortgageQuote, brand: BrandEnum, onApply: (id: number, quoteId: number) => void }) {
    const isBrandLogo =
        (params.brand === BrandEnum.LowQuote && params.item.isUsingLowQuoteLogo) ||
        (params.brand === BrandEnum.Low && params.item.isUsingLowLogo) ||
        (params.brand === BrandEnum.Hello && params.item.isUsingHelloLogo) ||
        (params.brand === BrandEnum.TrueWealth && params.item.isUsingTrueWealthLogo);

    const rateLabel = isGreenMortgage(params.item.mortgageName)
        ? "Green " + (params.item.fixedPeriod === 0 ? "Variable rate" : `${params.item.fixedPeriod / 12} years fixed`)
        : params.item.fixedPeriod === 0
            ? "Variable rate"
            : `${params.item.fixedPeriod / 12} years fixed`;

    return (
        <Card sx={{ marginBottom: 2 }}>
            <Box display="flex" justifyContent="space-between">
                <Box display="flex" gap={1} alignItems="center" paddingBottom={1}>
                    <Avatar
                        variant="square"
                        sx={{
                            bgcolor: "transparent",
                            padding: 0,
                            width: 84,
                            height: 84,
                        }}
                    >
                        {isBrandLogo ? (
                            <img
                                src={getBrandLogoSymbol(params.brand)}
                                alt="Logo"
                                style={{ width: "100%", height: "100%", objectFit: "contain" }}
                            />
                        ) : (
                                <QuoteProviderLogoView providerId={params.item.providerId} />
                        )}
                    </Avatar>

                    <Typography variant="body1">{rateLabel}</Typography>

                    <Box marginLeft={2}>
                        <Typography
                            variant="body1"
                            sx={{
                                bgcolor: getBrandVibrantColor(params.brand),
                                color: getBrandPrimaryColor(params.brand),
                                paddingLeft: 1,
                                paddingRight: 1,
                                borderRadius: 1,
                            }}
                        >
                            Online Account Available
                        </Typography>
                    </Box>
                </Box>

                {isGreenMortgage(params.item.mortgageName) && (
                    <Box display="flex" gap={1} alignItems="center">
                        <Typography variant="body2">Green Mortgage</Typography>
                        <Info fontSize="small" color="primary" />
                    </Box>
                )}
            </Box>

            <Divider />

            <CardContent>
                <Box display="flex" marginTop={1}>
                    <>
                        <Box display="flex" flexDirection="column">
                            <Typography variant="body1">{params.item.rate}%</Typography>
                            <Typography variant="body2" color="text.secondary">
                                {params.item.fixedPeriod === 0 ? "Variable rate" : `${params.item.fixedPeriod / 12} years fixed rate`}
                            </Typography>
                        </Box>

                        <Box sx={useBoxStyles.verticalLineBox} />

                        <Box display="flex" flexDirection="column">
                            <Typography variant="body1">{params.item.maxLoanToValue}%</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Max LTV
                            </Typography>
                        </Box>

                        <Box sx={useBoxStyles.verticalLineBox} />

                        <Box display="flex" flexDirection="column">
                            <Typography variant="body1">{params.item.apr}%</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Indicative APRC*
                            </Typography>
                        </Box>

                        <Box sx={useBoxStyles.verticalLineBox} />

                        <Box display="flex" flexDirection="column">
                            <Typography variant="body1">{getMoneyFormat(params.item.monthlyPayment)}</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Monthly Payment
                            </Typography>
                        </Box>
                    </>

                    <Box display="flex" flexDirection="column" gap={1} marginLeft={2}>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => window.open(getBrandCallbackLink(params.brand), "_blank", "noopener,noreferrer")}
                        >
                            <Typography variant="subtitle2">Schedule an appointment</Typography>
                        </Button>

                        <Button
                            variant="contained"
                            onClick={() => params.onApply(params.item.id, params.item.quoteId)}
                            sx={{ bgcolor: getBrandSecondaryColor(params.brand) }}
                            size="small"
                        >
                            <Typography variant="subtitle2">Apply now</Typography>
                        </Button>
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
}
